import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';
import { Button, type ButtonProps, composeRenderProps } from 'react-aria-components';
import { makeTestID } from 'util/stdtypes';

import styles from './action-button.module.css';

const button = cva(styles.button, {
    variants: {
        iconOnly: { true: styles.iconOnly },
    },
});

/** @public */
export interface ActionButtonProps extends Omit<ButtonProps, 'children'> {
    icon?: React.ComponentType<{ size?: number; color?: string }>;
    /** Pre-translated text. Call `useTranslation(label)` in the parent. */
    text?: string;
    testID?: string;
}

export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(({ icon: Icon, text, testID, className, ...rest }, ref) => {
    const iconOnly = Boolean(!text && Icon);
    // composeRenderProps: see Dev Notes 'Extending wrapper styles'.
    const composedClassName = composeRenderProps(className, (callerClass) => button({ iconOnly, className: callerClass }));
    return (
        <Button data-testid={makeTestID({ testID, text })} ref={ref} {...rest} className={composedClassName}>
            {Icon && <Icon size={16} />}
            {text && <span>{text}</span>}
        </Button>
    );
});
ActionButton.displayName = 'ActionButton';
